import { flash, Tabs } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { Coupon, PurchaseRule } from "types";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { CouponDescriptionForm } from "./CouponDescriptionForm";
import { SubmitCouponParams } from "./CouponEditorProps";
import { CouponRestrictionForm } from "./CouponRestrictionForm";
import { CreateCouponCodesForm } from "./CreateCouponCodesForm";
import { EditCouponCodesForm } from "./EditCouponCodesForm";

interface EditCouponProps {
    coupon: Coupon;
}

export const EditCoupon = ({ coupon }: EditCouponProps) => {
    const history = useHistory();
    const { id, section } = useParams<{ id?: ID; section?: string }>();

    const [couponState, setCouponState] = useState(coupon);
    const invalidate = useInvalidate();

    const initialSection = section ?? tabIds[0].id;
    const initialActiveIndex = tabIds.findIndex((tab) => tab.id === section ?? initialSection);
    const [activeTab, setActiveTab] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (initialActiveIndex >= 0 && activeTab !== initialActiveIndex) {
            setActiveTab(initialActiveIndex);
        }
    }, [initialActiveIndex, activeTab]);

    useEffect(() => {
        setCouponState(coupon);
    }, [coupon]);

    const { mutateAsync: saveCoupon, isLoading: isSaving } = useMutation(
        async ({ coupon, duplicate }: SubmitCouponParams) => {
            if (coupon.id) {
                await api.put(`/coupons/${coupon.id}`, coupon);
                await api.put(`/purchaseRules/${coupon.purchaseRulePermanentId}`, coupon.purchaseRule);
            } else {
                const { data: persistedCoupon } = await api.post<Coupon>(
                    "/coupons",
                    { ...coupon, duplicate },
                    { params: { expand: "purchaseRule" } },
                );

                let purchaseRuleData;
                if (!persistedCoupon.purchaseRule) {
                    // Call the API only when persistedCoupon.purchaseRule is null, incase of code more than 1000
                    const { data } = await api.get<PurchaseRule>(
                        `/purchaseRules/${persistedCoupon.purchaseRulePermanentId}`,
                    );
                    purchaseRuleData = data;
                }

                // For new coupons, the API is creating the purchase rule object automatically, if no. of code is less than 1000.
                // In order to update the purchase rule with rules set in the UI when the user was creating the coupon,
                // we need to apply the changes from the UI to the recently persisted purchase rule.
                // Only field that we need to update is action's `shortcut`.
                // Field `filter` is not set by the API when the coupon is created, so we're setting it now.
                const [persistedAction] = purchaseRuleData?.actions ?? persistedCoupon.purchaseRule?.actions ?? [];
                const [createdAction] = coupon.purchaseRule?.actions ?? [];
                const purchaseRule = purchaseRuleData ?? persistedCoupon.purchaseRule ?? [];

                if (createdAction || coupon.purchaseRule?.filter) {
                    await api.put(`/purchaseRules/${persistedCoupon.purchaseRulePermanentId}`, {
                        ...purchaseRule,
                        actions: [{ ...persistedAction, shortcut: createdAction?.shortcut }],
                        filter: coupon.purchaseRule?.filter,
                    });
                }
            }

            flash.success("Coupon saved");
            invalidate("coupons", coupon.id);
            invalidate("codes");
            invalidate("couponCodes");
            invalidate("purchaseRules", coupon.purchaseRulePermanentId);
            if (!coupon.id) {
                history.push("/marketing/coupons");
            }
        },
    );

    const handleChangeTab = (tabIndex: number) => {
        // Don't allow switching tabs when creating a coupon
        if (coupon.id) {
            history.push(`/marketing/coupons/${id}/${tabIds[tabIndex].id}`);
        }
    };

    const handleSubmit = async ({ coupon, duplicate }: SubmitCouponParams) => {
        if (coupon.id || activeTab >= 2) {
            return saveCoupon({ coupon, duplicate });
        }

        setCouponState(coupon);
        setActiveTab(activeTab + 1);
    };

    const handleCancel = () => {
        if (activeTab === 0 || coupon.id) {
            history.push("/marketing/coupons");
            return;
        }

        setActiveTab(activeTab - 1);
    };

    return (
        <Tabs value={activeTab} onChange={handleChangeTab}>
            <Tabs.Tab>Description</Tabs.Tab>
            <Tabs.Panel>
                <CouponDescriptionForm
                    coupon={couponState}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            </Tabs.Panel>

            <Tabs.Tab>Restrictions</Tabs.Tab>
            <Tabs.Panel>
                <CouponRestrictionForm
                    coupon={couponState}
                    isSaving={isSaving}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            </Tabs.Panel>

            <Tabs.Tab>Codes</Tabs.Tab>
            <Tabs.Panel>
                {coupon.id ? (
                    <EditCouponCodesForm coupon={coupon} onCancel={handleCancel} />
                ) : (
                    <CreateCouponCodesForm
                        coupon={couponState}
                        isSaving={isSaving}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                )}
            </Tabs.Panel>
        </Tabs>
    );
};

const tabIds = [
    {
        id: "description",
    },
    {
        id: "restrictions",
    },
    {
        id: "codes",
    },
];
